import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio, PlusCircle, FilterIcon, Search, Wifi, WifiOff,
  Tag, Shirt, Trash2, Edit, Eye, Volume2, VolumeX, RefreshCw, Layers, X, CheckCircle2, ArrowLeft,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import DataTable from '@/components/DataTable';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  createRegistroPrendaRfid,
  updateRegistroPrendaRfid,
  deleteRegistroPrendaRfid,
  fetchRegistrosPrendasRfid,
  findByTagRfid,
} from '@/services/registroPrendasRfid.service';
import { fetchClients } from '@/pages/parametrizacion/clients/Services/clients.services';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ESTADOS = [
  { value: 'activo', label: 'Activo', color: 'bg-green-100 text-green-800' },
  { value: 'inactivo', label: 'Inactivo', color: 'bg-gray-100 text-gray-800' },
  { value: 'perdido', label: 'Perdido', color: 'bg-red-100 text-red-800' },
  { value: 'dado_de_baja', label: 'Dado de baja', color: 'bg-orange-100 text-orange-800' },
];

const getEstadoBadge = (estado) => {
  const e = ESTADOS.find((s) => s.value === estado);
  return e ? (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${e.color}`}>
      {e.label}
    </span>
  ) : estado;
};

const RegistroPrendasRFIDPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Table state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ query: {}, page: 1, limit: 20 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  // RFID Reader state
  const [isReading, setIsReading] = useState(false);
  const [lastTag, setLastTag] = useState('');
  const [tagBuffer, setTagBuffer] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [tagInfo, setTagInfo] = useState(null);
  const [tagSearching, setTagSearching] = useState(false);

  // Batch mode state
  const [batchMode, setBatchMode] = useState(false);
  const [batchConfig, setBatchConfig] = useState({
    cliente: null,
    tipoPrenda: null,
    lavanderia: null,
    color: '',
    talla: '',
    descripcion: '',
  });
  const [batchRegistered, setBatchRegistered] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    tagRfid: '',
    epc: '',
    tipoPrenda: null,
    cliente: null,
    lavanderia: null,
    descripcion: '',
    color: '',
    talla: '',
    estado: 'activo',
    observaciones: '',
  });

  // Options
  const [clientes, setClientes] = useState([]);
  const [tiposPrenda, setTiposPrenda] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Refs
  const rfidInputRef = useRef(null);
  const audioRef = useRef(null);
  const bufferTimeoutRef = useRef(null);

  // ========== RFID READER LOGIC ==========
  // El lector USB HID de escritorio emula un teclado: envía los caracteres del tag
  // seguidos de Enter. Capturamos eso con un input oculto.

  useEffect(() => {
    if (isReading && rfidInputRef.current) {
      rfidInputRef.current.focus();
    }
  }, [isReading]);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      osc.type = 'sine';
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {
      // Audio not available
    }
  }, [soundEnabled]);

  // ========== DATA FETCHING ==========

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchRegistrosPrendasRfid(filters);
      setData(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los registros.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRfidInput = useCallback(async (e) => {
    if (e.key === 'Enter' && tagBuffer.trim()) {
      const tag = tagBuffer.trim();
      setLastTag(tag);
      setTagBuffer('');
      playBeep();

      // Modo lote: registrar automáticamente
      if (batchMode && batchConfig.cliente && batchConfig.tipoPrenda) {
        setBatchProcessing(true);
        try {
          const result = await findByTagRfid(tag);
          if (result?.data) {
            toast({
              title: 'Tag ya registrado',
              description: `"${tag}" ya está asignado a ${result.data.tipoPrenda?.nombre || ''} - ${result.data.cliente?.nombre || ''}`,
              variant: 'destructive',
            });
          } else {
            const payload = {
              tagRfid: tag,
              epc: tag,
              ...batchConfig,
              estado: 'activo',
              registradoPor: user?.name || 'Sistema',
            };
            const createResult = await createRegistroPrendaRfid(payload);
            if (createResult?.code === 201 || createResult?.data) {
              setBatchRegistered((prev) => [{ tag, time: new Date() }, ...prev]);
              toast({
                title: `✓ Tag registrado (${batchRegistered.length + 1})`,
                description: `${batchConfig.tipoPrenda.nombre} → ${batchConfig.cliente.nombre}`,
              });
              fetchData();
            } else {
              toast({ title: 'Error', description: createResult?.message || 'Error al registrar', variant: 'destructive' });
            }
          }
        } catch (error) {
          toast({ title: 'Error', description: error.message || 'Error en registro por lote', variant: 'destructive' });
        } finally {
          setBatchProcessing(false);
          setTimeout(() => rfidInputRef.current?.focus(), 100);
        }
        return;
      }

      // Modo normal: buscar y abrir modal si es nuevo
      setTagSearching(true);
      try {
        const result = await findByTagRfid(tag);
        if (result?.data) {
          setTagInfo(result.data);
          toast({
            title: 'Tag encontrado',
            description: `Prenda: ${result.data.tipoPrenda?.nombre || 'N/A'} - Cliente: ${result.data.cliente?.nombre || 'N/A'}`,
          });
        } else {
          setTagInfo(null);
          toast({
            title: 'Tag nuevo',
            description: `El tag "${tag}" no está registrado. Puedes registrarlo como nueva prenda.`,
          });
          setFormData((prev) => ({ ...prev, tagRfid: tag, epc: tag }));
          setIsCreateModalOpen(true);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Error al buscar el tag RFID',
          variant: 'destructive',
        });
      } finally {
        setTagSearching(false);
      }
    }
  }, [tagBuffer, playBeep, toast, batchMode, batchConfig, batchRegistered, user, fetchData]);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [clientsRes, garmentRes] = await Promise.all([
        fetchClients({ limit: 500, offset: 1, query: { estado: 'Activo' } }),
        getGarmentTypes({ page: 1, limit: 500, query: {} }),
      ]);
      setClientes(clientsRes?.data || []);
      setTiposPrenda(garmentRes?.data || []);
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // ========== HANDLERS ==========

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, query: {}, page: 1 }));
  };

  const handleRemoveFilter = (filterKey) => {
    setFilters((prev) => {
      const newQuery = { ...prev.query };
      delete newQuery[filterKey];
      return { ...prev, query: newQuery, page: 1 };
    });
  };

  const resetForm = () => {
    setFormData({
      tagRfid: '',
      epc: '',
      tipoPrenda: null,
      cliente: null,
      lavanderia: null,
      descripcion: '',
      color: '',
      talla: '',
      estado: 'activo',
      observaciones: '',
    });
  };

  const handleCreate = async () => {
    if (!formData.tagRfid) {
      toast({ title: 'Error', description: 'El tag RFID es obligatorio', variant: 'destructive' });
      return;
    }
    if (!formData.cliente) {
      toast({ title: 'Error', description: 'Debe seleccionar un cliente', variant: 'destructive' });
      return;
    }
    if (!formData.tipoPrenda) {
      toast({ title: 'Error', description: 'Debe seleccionar un tipo de prenda', variant: 'destructive' });
      return;
    }

    try {
      const payload = {
        ...formData,
        registradoPor: user?.name || 'Sistema',
      };
      const result = await createRegistroPrendaRfid(payload);
      if (result?.code === 201 || result?.data) {
        toast({ title: 'Registro creado', description: result.message || 'Prenda registrada exitosamente' });
        setIsCreateModalOpen(false);
        resetForm();
        fetchData();
      } else {
        toast({ title: 'Error', description: result?.message || 'Error al crear registro', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Error al crear registro', variant: 'destructive' });
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem?._id) return;
    try {
      const result = await updateRegistroPrendaRfid(selectedItem._id, formData);
      if (result?.data) {
        toast({ title: 'Actualizado', description: 'Registro actualizado exitosamente' });
        setIsEditModalOpen(false);
        resetForm();
        setSelectedItem(null);
        fetchData();
      } else {
        toast({ title: 'Error', description: result?.message || 'Error al actualizar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?._id) return;
    try {
      await deleteRegistroPrendaRfid(itemToDelete._id);
      toast({ title: 'Eliminado', description: 'Registro eliminado exitosamente' });
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      tagRfid: item.tagRfid || '',
      epc: item.epc || '',
      tipoPrenda: item.tipoPrenda || null,
      cliente: item.cliente || null,
      lavanderia: item.lavanderia || null,
      descripcion: item.descripcion || '',
      color: item.color || '',
      talla: item.talla || '',
      estado: item.estado || 'activo',
      observaciones: item.observaciones || '',
    });
    setIsEditModalOpen(true);
  };

  const openDetail = (item) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  };

  // ========== MANUAL TAG SEARCH ==========
  const [manualTag, setManualTag] = useState('');

  const handleManualSearch = async () => {
    if (!manualTag.trim()) return;
    setTagSearching(true);
    try {
      const result = await findByTagRfid(manualTag.trim());
      if (result?.data) {
        setTagInfo(result.data);
        setLastTag(manualTag.trim());
        playBeep();
        toast({
          title: 'Tag encontrado',
          description: `Prenda: ${result.data.tipoPrenda?.nombre || 'N/A'}`,
        });
      } else {
        setTagInfo(null);
        setLastTag(manualTag.trim());
        toast({
          title: 'Tag nuevo',
          description: `El tag no está registrado.`,
        });
        setFormData((prev) => ({ ...prev, tagRfid: manualTag.trim(), epc: manualTag.trim() }));
        setIsCreateModalOpen(true);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error al buscar', variant: 'destructive' });
    } finally {
      setTagSearching(false);
      setManualTag('');
    }
  };

  // ========== COLUMNS ==========
  const columns = [
    {
      id: 'tagRfid',
      label: 'Tag RFID',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-blue-500" />
          <span className="font-mono text-xs">{row.tagRfid}</span>
        </div>
      ),
    },
    {
      id: 'tipoPrenda',
      label: 'Tipo Prenda',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shirt className="h-4 w-4 text-purple-500" />
          <span>{row.tipoPrenda?.nombre || '-'}</span>
        </div>
      ),
    },
    {
      id: 'cliente',
      label: 'Cliente',
      render: (row) => row.cliente?.nombre || '-',
    },
    {
      id: 'color',
      label: 'Color',
    },
    {
      id: 'talla',
      label: 'Talla',
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => getEstadoBadge(row.estado),
    },
    {
      id: 'ultimaLectura',
      label: 'Última Lectura',
      render: (row) => {
        const d = row.ultimaLectura;
        return d ? new Date(d).toLocaleString('es-CO') : '-';
      },
    },
    {
      id: 'totalLecturas',
      label: 'Lecturas',
      render: (row) => (
        <Badge variant="outline">{row.totalLecturas || 0}</Badge>
      ),
    },
    {
      id: 'opciones',
      label: 'Acciones',
      render: (row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => openDetail(row)} title="Ver detalle">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Editar">
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setItemToDelete(row); setIsDeleteDialogOpen(true); }}
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  const columnsExcel = [
    { header: 'Tag RFID', accessorKey: 'tagRfid' },
    { header: 'EPC', accessorKey: 'epc' },
    { header: 'Tipo Prenda', accessorKey: 'tipoPrenda.nombre' },
    { header: 'Cliente', accessorKey: 'cliente.nombre' },
    { header: 'Color', accessorKey: 'color' },
    { header: 'Talla', accessorKey: 'talla' },
    { header: 'Estado', accessorKey: 'estado' },
    { header: 'Descripción', accessorKey: 'descripcion' },
    { header: 'Registrado Por', accessorKey: 'registradoPor' },
  ];

  const filterFields = [
    {
      name: 'tagRfid',
      label: 'Tag RFID',
      type: 'text',
    },
    {
      name: 'estado',
      label: 'Estado',
      type: 'select',
      options: ESTADOS.map((e) => ({ label: e.label, value: e.value })),
    },
    {
      name: 'cliente.nombre',
      label: 'Cliente',
      type: 'text',
    },
    {
      name: 'tipoPrenda.nombre',
      label: 'Tipo de prenda',
      type: 'text',
    },
  ];

  // ========== FORM MODAL (shared between create/edit) ==========
  const renderForm = (isEdit = false) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label>Tag RFID *</Label>
        <Input
          value={formData.tagRfid}
          onChange={(e) => setFormData((p) => ({ ...p, tagRfid: e.target.value }))}
          placeholder="Código del tag RFID"
          disabled={isEdit}
          className="font-mono"
        />
      </div>

      <div className="space-y-2">
        <Label>EPC</Label>
        <Input
          value={formData.epc}
          onChange={(e) => setFormData((p) => ({ ...p, epc: e.target.value }))}
          placeholder="Código EPC del tag"
          className="font-mono"
        />
      </div>

      <div className="space-y-2">
        <Label>Cliente *</Label>
        <Select
          value={formData.cliente?.id || ''}
          onValueChange={(val) => {
            const c = clientes.find((x) => x._id === val);
            if (c) {
              setFormData((p) => ({
                ...p,
                cliente: { id: c._id, nombre: c.nombre },
                lavanderia: c.lavanderia ? { id: c.lavanderia.id || c.lavanderia._id, nombre: c.lavanderia.nombre } : p.lavanderia,
              }));
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar cliente" />
          </SelectTrigger>
          <SelectContent>
            {clientes.map((c) => (
              <SelectItem key={c._id} value={c._id}>
                {c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Tipo de Prenda *</Label>
        <Select
          value={formData.tipoPrenda?.id || ''}
          onValueChange={(val) => {
            const t = tiposPrenda.find((x) => x._id === val);
            if (t) {
              setFormData((p) => ({
                ...p,
                tipoPrenda: { id: t._id, nombre: t.nombre },
              }));
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar tipo de prenda" />
          </SelectTrigger>
          <SelectContent>
            {tiposPrenda.map((t) => (
              <SelectItem key={t._id} value={t._id}>
                {t.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Color</Label>
        <Input
          value={formData.color}
          onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))}
          placeholder="Color de la prenda"
        />
      </div>

      <div className="space-y-2">
        <Label>Talla</Label>
        <Input
          value={formData.talla}
          onChange={(e) => setFormData((p) => ({ ...p, talla: e.target.value }))}
          placeholder="Talla (S, M, L, XL...)"
        />
      </div>

      <div className="space-y-2">
        <Label>Estado</Label>
        <Select
          value={formData.estado}
          onValueChange={(val) => setFormData((p) => ({ ...p, estado: val }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ESTADOS.map((e) => (
              <SelectItem key={e.value} value={e.value}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Descripción</Label>
        <Input
          value={formData.descripcion}
          onChange={(e) => setFormData((p) => ({ ...p, descripcion: e.target.value }))}
          placeholder="Descripción adicional"
        />
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label>Observaciones</Label>
        <textarea
          className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={formData.observaciones}
          onChange={(e) => setFormData((p) => ({ ...p, observaciones: e.target.value }))}
          placeholder="Observaciones..."
        />
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
      >
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/rfid')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> RFID
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Radio className="h-7 w-7 text-blue-600" />
              Registro de Prendas RFID
            </h1>
            <p className="text-muted-foreground mt-1">
              Registra y gestiona prendas con tags RFID lavables
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setIsFilterOpen(true); }}
          >
            <FilterIcon className="h-4 w-4 mr-1" /> Filtros
          </Button>
          <ExportExcelButton data={data} columns={columnsExcel} fileName="registro_prendas_rfid" />
          <Button
            variant={batchMode ? 'default' : 'outline'}
            onClick={() => {
              if (!batchMode) {
                setBatchMode(true);
                setBatchRegistered([]);
              } else {
                setBatchMode(false);
              }
            }}
            className={batchMode ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            <Layers className="h-4 w-4 mr-1" /> {batchMode ? 'Modo Lote Activo' : 'Registro por Lote'}
          </Button>
          <Button onClick={() => { resetForm(); setIsCreateModalOpen(true); }}>
            <PlusCircle className="h-4 w-4 mr-1" /> Registrar Prenda
          </Button>
        </div>
      </motion.div>

      {/* RFID Reader Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              {/* Modo lectura automática (HID) */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  {isReading ? (
                    <Wifi className="h-5 w-5 text-green-500 animate-pulse" />
                  ) : (
                    <WifiOff className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">
                    {isReading ? 'Modo lectura activo - Acerca un tag al lector' : 'Lectura pausada'}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant={isReading ? 'destructive' : 'default'}
                    size="sm"
                    onClick={() => {
                      setIsReading(!isReading);
                      if (!isReading) {
                        setTimeout(() => rfidInputRef.current?.focus(), 100);
                      }
                    }}
                  >
                    <Radio className="h-4 w-4 mr-1" />
                    {isReading ? 'Detener Lectura' : 'Iniciar Lectura'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Refrescar
                  </Button>
                </div>

                {/* Input oculto para capturar lectura HID del lector USB */}
                <input
                  ref={rfidInputRef}
                  type="text"
                  value={tagBuffer}
                  onChange={(e) => setTagBuffer(e.target.value)}
                  onKeyDown={handleRfidInput}
                  className={`w-full px-3 py-2 rounded-md border text-sm font-mono ${
                    isReading
                      ? 'border-green-400 bg-green-50 dark:bg-green-950/30 focus:ring-green-500'
                      : 'border-gray-300 bg-gray-50 dark:bg-gray-900'
                  } focus:outline-none focus:ring-2`}
                  placeholder={isReading ? (batchMode && batchConfig.cliente && batchConfig.tipoPrenda ? `Modo Lote: ${batchConfig.tipoPrenda.nombre} → ${batchConfig.cliente.nombre} | Pasa el siguiente tag...` : 'Esperando lectura del tag RFID...') : 'Click en "Iniciar Lectura" para comenzar'}
                  readOnly={!isReading}
                  autoComplete="off"
                />
              </div>

              {/* Búsqueda manual */}
              <div className="w-full md:w-80 space-y-2">
                <span className="font-medium text-sm text-muted-foreground">Búsqueda manual</span>
                <div className="flex gap-2">
                  <Input
                    value={manualTag}
                    onChange={(e) => setManualTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                    placeholder="Ingresar tag manualmente..."
                    className="font-mono"
                  />
                  <Button size="sm" onClick={handleManualSearch} disabled={tagSearching}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Último tag leído */}
              {lastTag && (
                <div className="w-full md:w-auto">
                  <div className="text-xs text-muted-foreground mb-1">Último tag leído</div>
                  <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-md border px-3 py-2">
                    <Tag className="h-4 w-4 text-blue-500" />
                    <span className="font-mono text-sm font-medium">{lastTag}</span>
                    {tagInfo && (
                      <Badge variant="outline" className="ml-2 text-green-600 border-green-300">
                        Registrado
                      </Badge>
                    )}
                    {lastTag && !tagInfo && !tagSearching && (
                      <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300">
                        Nuevo
                      </Badge>
                    )}
                  </div>
                  {tagInfo && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {tagInfo.tipoPrenda?.nombre} - {tagInfo.cliente?.nombre} ({tagInfo.estado})
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Batch Mode Config */}
      <AnimatePresence>
        {batchMode && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-2 border-amber-400 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Layers className="h-5 w-5 text-amber-600" />
                    Registro por Lote — Configura y pasa los tags uno a uno
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setBatchMode(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  Selecciona cliente y tipo de prenda. Luego pasa cada tag por el lector y se registrará automáticamente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Cliente *</Label>
                    <Select
                      value={batchConfig.cliente?.id || ''}
                      onValueChange={(val) => {
                        const c = clientes.find((x) => x._id === val);
                        if (c) {
                          setBatchConfig((p) => ({
                            ...p,
                            cliente: { id: c._id, nombre: c.nombre },
                            lavanderia: c.lavanderia ? { id: c.lavanderia.id || c.lavanderia._id, nombre: c.lavanderia.nombre } : p.lavanderia,
                          }));
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map((c) => (
                          <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Tipo de Prenda *</Label>
                    <Select
                      value={batchConfig.tipoPrenda?.id || ''}
                      onValueChange={(val) => {
                        const t = tiposPrenda.find((x) => x._id === val);
                        if (t) {
                          setBatchConfig((p) => ({ ...p, tipoPrenda: { id: t._id, nombre: t.nombre } }));
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccionar prenda" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposPrenda.map((t) => (
                          <SelectItem key={t._id} value={t._id}>{t.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Color</Label>
                    <Input
                      value={batchConfig.color}
                      onChange={(e) => setBatchConfig((p) => ({ ...p, color: e.target.value }))}
                      placeholder="Color"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Talla</Label>
                    <Input
                      value={batchConfig.talla}
                      onChange={(e) => setBatchConfig((p) => ({ ...p, talla: e.target.value }))}
                      placeholder="Talla"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Descripción</Label>
                    <Input
                      value={batchConfig.descripcion}
                      onChange={(e) => setBatchConfig((p) => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Descripción"
                      className="h-9"
                    />
                  </div>
                  <div className="flex items-end">
                    {batchConfig.cliente && batchConfig.tipoPrenda ? (
                      <Badge className="bg-green-600 text-white h-9 flex items-center gap-1 px-3">
                        <CheckCircle2 className="h-4 w-4" /> Listo para escanear
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="h-9 flex items-center gap-1 px-3 text-amber-600 border-amber-400">
                        Faltan campos obligatorios
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Tags registrados en esta sesión de lote */}
                {batchRegistered.length > 0 && (
                  <div className="mt-3 border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        Tags registrados en esta sesión: <strong className="text-green-600">{batchRegistered.length}</strong>
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setBatchRegistered([])}>
                        Limpiar lista
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {batchRegistered.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="font-mono text-xs gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                          {item.tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {batchProcessing && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 animate-pulse">
                    <Radio className="h-4 w-4 animate-spin" /> Registrando tag...
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Applied Filters */}
      <AppliedFilters
        filters={filters.query}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearFilters}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalRecords}</div>
            <div className="text-xs text-muted-foreground">Total Registros</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {data.filter((d) => d.estado === 'activo').length}
            </div>
            <div className="text-xs text-muted-foreground">Activas (página)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {data.filter((d) => d.estado === 'perdido').length}
            </div>
            <div className="text-xs text-muted-foreground">Perdidas (página)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {lastTag ? 1 : 0}
            </div>
            <div className="text-xs text-muted-foreground">Última Lectura</div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shirt className="h-5 w-5" />
            Prendas Registradas
          </CardTitle>
          <CardDescription>
            {totalRecords} prendas con tag RFID registradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data}
            isLoading={loading}
            totalRecords={totalRecords}
            page={filters.page}
            limit={filters.limit}
            onPageChange={(page) => setFilters((p) => ({ ...p, page }))}
            onLimitChange={(limit) => setFilters((p) => ({ ...p, limit, page: 1 }))}
          />
        </CardContent>
      </Card>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        fields={filterFields}
        onFilter={handleFilter}
        currentFilters={filters.query}
      />

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-blue-600" />
              Registrar Nueva Prenda RFID
            </DialogTitle>
            <DialogDescription>
              Asocia un tag RFID a una prenda y cliente
            </DialogDescription>
          </DialogHeader>
          {renderForm(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreate}>
              <PlusCircle className="h-4 w-4 mr-1" /> Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Editar Registro RFID
            </DialogTitle>
            <DialogDescription>
              Modifica la información asociada al tag
            </DialogDescription>
          </DialogHeader>
          {renderForm(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditModalOpen(false); resetForm(); setSelectedItem(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Detalle de Prenda RFID
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Tag RFID</span>
                  <p className="font-mono font-medium">{selectedItem.tagRfid}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">EPC</span>
                  <p className="font-mono font-medium">{selectedItem.epc || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Tipo de Prenda</span>
                  <p className="font-medium">{selectedItem.tipoPrenda?.nombre || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Cliente</span>
                  <p className="font-medium">{selectedItem.cliente?.nombre || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Color</span>
                  <p>{selectedItem.color || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Talla</span>
                  <p>{selectedItem.talla || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Estado</span>
                  <p>{getEstadoBadge(selectedItem.estado)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Lecturas</span>
                  <p className="font-medium">{selectedItem.totalLecturas || 0}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Última Lectura</span>
                  <p>{selectedItem.ultimaLectura ? new Date(selectedItem.ultimaLectura).toLocaleString('es-CO') : '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Fecha Registro</span>
                  <p>{selectedItem.fechaRegistro ? new Date(selectedItem.fechaRegistro).toLocaleString('es-CO') : '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Registrado Por</span>
                  <p>{selectedItem.registradoPor || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Lavandería</span>
                  <p>{selectedItem.lavanderia?.nombre || '-'}</p>
                </div>
              </div>
              {selectedItem.descripcion && (
                <div>
                  <span className="text-muted-foreground text-sm">Descripción</span>
                  <p className="text-sm">{selectedItem.descripcion}</p>
                </div>
              )}
              {selectedItem.observaciones && (
                <div>
                  <span className="text-muted-foreground text-sm">Observaciones</span>
                  <p className="text-sm">{selectedItem.observaciones}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}>
              Cerrar
            </Button>
            <Button onClick={() => { setIsDetailModalOpen(false); openEdit(selectedItem); }}>
              <Edit className="h-4 w-4 mr-1" /> Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Registro</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar el registro del tag <strong className="font-mono">{itemToDelete?.tagRfid}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RegistroPrendasRFIDPage;
